// Create a Municipal Officer account. POST /admin/users/official.
//
// Field workers go through CreateWorkerModal (POST /workers/) instead: that is
// the endpoint built for them, and it takes the skill set this form has no
// place for. Sending role=field_worker here is refused with a 400.
//
// Department and designation used to be free text, which is how the same job
// ended up recorded as "Roads", "roads & transport" and "Roads and Transport".
// Department decides which complaints an officer sees, so a typo there is not
// cosmetic — it quietly detaches them from their own queue. Both are now
// chosen from the real sets.
//
// The password is optional and generated by default; see NewAccountPassword.
// Creation ends on a second screen reporting whether the new officer was
// actually told how to sign in, which is a separate fact from the account
// existing.
import React, { useMemo, useState } from 'react';
import Modal from './Modal';
import CredentialDeliveryNote from './CredentialDeliveryNote';
import {
  TextField, SelectField, FormError, SubmitRow, DESIGNATIONS,
  NewAccountPassword, MIN_NEW_PASSWORD,
} from './formFields';
import { createOfficer } from '../../api/admin';
import { skillOptions } from '../../lib/skills';
import useCategories from '../../hooks/useCategories';

export default function CreateOfficerModal({ onDismiss, onCreated }) {
  const { categories } = useCategories();
  // The same list the complaints are routed by, so the two cannot drift.
  const departments = useMemo(() => skillOptions(categories), [categories]);

  const [form, setForm] = useState({
    name: '', email: '', phone: '', password: '',
    department: '', designation: 'Grievance Officer',
  });
  const [generatePassword, setGeneratePassword] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setError('Name and email are both required.');
      return;
    }
    if (!generatePassword && form.password.length < MIN_NEW_PASSWORD) {
      setError(`A password you choose needs at least ${MIN_NEW_PASSWORD} characters.`);
      return;
    }
    if (!form.department) {
      setError('Choose a department — it decides which complaints this officer sees.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      const result = await createOfficer({
        ...form,
        password: generatePassword ? undefined : form.password,
      });
      setCreated(result);
    } catch (err) {
      if (err.name !== 'SessionExpiredError') setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const finish = () => onCreated(`${form.name} added as a Municipal Officer.`);

  if (created) {
    return (
      <Modal title="Officer created" onDismiss={finish}>
        <div className="space-y-3.5">
          <p className="text-[13.5px] leading-snug text-ink-body">
            <strong className="text-ink">{form.name}</strong> is recorded as{' '}
            {form.designation} in {form.department}. Complaints filed against that
            department's categories are routed to them from now on.
          </p>

          <CredentialDeliveryNote
            delivery={created.credentialDelivery}
            name={form.name}
          />

          <button
            type="button"
            onClick={finish}
            className="focus-ring w-full rounded-lg bg-leaf-600 py-2.5 text-[13.5px] font-semibold text-white shadow-sm transition-colors hover:bg-leaf-700"
          >
            Done
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Add Municipal Officer" onDismiss={onDismiss}>
      <form onSubmit={submit} className="space-y-3.5">
        <TextField
          id="of-name"
          label="Full name"
          required
          value={form.name}
          onChange={set('name')}
        />
        <TextField
          id="of-email"
          label="Email"
          type="email"
          required
          value={form.email}
          onChange={set('email')}
          hint="This is how they sign in, and where their password is sent. It must be unique."
        />
        <TextField
          id="of-phone"
          label="Phone"
          value={form.phone}
          onChange={set('phone')}
        />

        <NewAccountPassword
          idPrefix="of"
          generate={generatePassword}
          onGenerateChange={setGeneratePassword}
          value={form.password}
          onChange={set('password')}
        />

        <SelectField
          id="of-department"
          label="Department"
          required
          value={form.department}
          onChange={set('department')}
          options={departments}
          placeholder="Choose a department…"
          hint="Decides which complaints this officer is responsible for."
        />

        <SelectField
          id="of-designation"
          label="Designation"
          value={form.designation}
          onChange={set('designation')}
          options={DESIGNATIONS}
          placeholder="Choose a designation…"
        />

        <FormError>{error}</FormError>

        <SubmitRow
          busy={saving}
          label="Create officer"
          busyLabel="Creating…"
          onCancel={onDismiss}
        />
      </form>
    </Modal>
  );
}
